import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { Edit, Delete, Visibility } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";
import FallbackImage from "../components/FallbackImage";
import { getImageFullUrl, pageRoute } from "../utils/common";
import { useNavigate } from "react-router-dom";

const UserListPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate()

  const pageChange = (url) => {
    navigate(url)
  }

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/api/admin/user", {
        params: {
          search,
          status: statusFilter,
          page: page + 1, // backend usually 1-based
          limit: rowsPerPage,
        },
      });

      setProducts(res?.data?.data?.users || []);
      setTotalCount(res?.data?.data?.total || 0);
    } catch (err) {
      toast.error("Failed to load user");
    } finally {
      setLoading(false);
    }
  };

  // Refetch when filters/search/page/rowsPerPage change
  useEffect(() => {
    fetchProducts();
  }, [search, statusFilter, page, rowsPerPage]);

  // Toggle status
  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiClient.put(`/api/admin/user/${id}`, { status: newStatus });
      toast.success("Status updated");
      fetchProducts();
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" gutterBottom>
          User List
        </Typography>
        
      </Box>
      {/* Search & Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => {
            setPage(0); // reset page
            setSearch(e.target.value);
          }}
        />
        <TextField
          select
          size="small"
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => {
            setPage(0);
            setStatusFilter(e.target.value);
          }}
          sx={{ width: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Avatar</TableCell>
              <TableCell> Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {products.length > 0 ? (
              products.map((row) => (
                <TableRow key={row.id}>
                  <TableCell><FallbackImage
                    src={getImageFullUrl(row.avatar)}
                    style={{ maxHeight: '50px' }}
                  /></TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.mobile}</TableCell>
                  <TableCell>{row.city}</TableCell>
                  <TableCell>{row.state}</TableCell>
                  <TableCell>
                    <Switch
                      checked={row.status === "active"}
                      onChange={(e) =>
                        handleStatusChange(
                          row.id,
                          e.target.checked ? "active" : "inactive"
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {loading ? "Loading..." : "No products found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 20]}
      />
    </Paper>
  );
};

export default UserListPage;
